import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { SubSink } from 'subsink';
import { BaseFile, ServiceLineSummaryDTO, UploadError } from "@shared/model";
import { PacimUloadFilesDialog } from '../pacim-upload-files-dialog/pacim-upload-files-dialog.component';
import { UploadDialogConfig } from '../upload-dialog-config.interface';
import { ToastMessageOptions } from 'primeng/api';
import { UploadDialogAction } from '../upload-dialog-actions.enum';

@Component({
    selector: 'document-select-upload',
    templateUrl: './document-select-upload.component.html',
    styleUrls: ['./document-select-upload.component.scss'],
    standalone: false
})
export class DocumentSelectUploadComponent {
  @Input() multiple!: boolean;
  @Input() accept!: string;
  @Input() maxFileSize!: number;
  @Input() shouldShowCSPopup = false;
  @Input() authType!: string;
  @Input() serviceLinesSummary?: ServiceLineSummaryDTO[];
  @Input() isPacCommercial!: boolean;
  @Input() authDocuments?: BaseFile[];
  @Input() programState!: string;
  @Input() crsMessage: ToastMessageOptions = {};

  @Output() selectFiles = new EventEmitter<File[]>();
  @Output() errorValidation = new EventEmitter<UploadError[]>();
  @Output() createContinuedStay = new EventEmitter<void>();
  @Output() levelAdjustment = new EventEmitter<void>();

  @ViewChild('inputFile', { static: true }) inputFile!: ElementRef<HTMLInputElement>;

  private files: File[] = [];
  private errors: UploadError[] = [];

  private subs = new SubSink();

  constructor(
    public readonly sanitizer: DomSanitizer,
    private dialogService: DialogService,
  ) {}

  addFile(): void {
    if (this.shouldShowCSPopup) {
      this.showCSPopup();
    } else {
      this.inputFile.nativeElement.click();
    }
  }

  onFileSelect(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files: File[] = Array.from<File>(target?.files as FileList);
    // Clear old errors
    this.errors = [];

    this.files = files.reduce((acc: File[], file: File) => {
      if (this.validate(file)) {
        if (this.isImage(file)) {
          // image preview
          (file as any).objectURL = this.sanitizer.bypassSecurityTrustUrl(
            window.URL.createObjectURL(file)
          );
        }
        acc.push(file);
      }
      return acc;
    }, []);

    if (this.errors.length) {
      this.errorValidation.emit(this.errors);
      return;
    }

    this.selectFiles.emit(this.files);
    if (target) {
      target.value = '';
    }
  }

  validate(file: File): boolean {
    // File type check
    if (this.accept && !this.isFileTypeValid(file)) {
      this.errors.push({ type: 'accept', name: file.name });
      return false;
    }

    // File size check
    if (this.maxFileSize && file.size > this.maxFileSize) {
      this.errors.push({ type: 'fileSize', name: file.name });
      return false;
    }

    return true;
  }

  isFileTypeValid(file: File): boolean {
    const acceptableTypes = this.accept.split(',').map((type) => type.trim());

    for (const type of acceptableTypes) {
      const acceptable = this.isWildcard(type)
        ? this.getTypeClass(file.type) === this.getTypeClass(type)
        : file.type === type ||
          this.getFileExtension(file).toLowerCase() === type.toLowerCase();

      if (acceptable) {
        return true;
      }
    }

    return false;
  }

  getTypeClass(fileType: string): string {
    return fileType.substring(0, fileType.indexOf('/'));
  }

  isWildcard(fileType: string): boolean {
    return fileType.indexOf('*') !== -1;
  }

  getFileExtension(file: File): string {
    return '.' + file.name.split('.').pop();
  }

  isImage(file: File): boolean {
    return /^image\//.test(file.type);
  }

  private showCSPopup(): void {
    const dialogConfig: DynamicDialogConfig<UploadDialogConfig> = {
      width: '500px',
      modal: true,
      header: 'Choose Next Action',
      data: {
        serviceLines: this.serviceLinesSummary,
        isPacCommercial: this.isPacCommercial,
        documents: this.authDocuments,
        hasLevelAction: this.isPacCommercial ? this.authType !== 'IRF' : this.authType === 'SNF',
        programState: this.programState,
        message: this.crsMessage,
      },
      closable: true,
    };

    const ref = this.dialogService.open(PacimUloadFilesDialog, dialogConfig);

    this.subs.sink = ref.onClose.subscribe((action: UploadDialogAction | undefined) => {
      switch (action) {
        case UploadDialogAction.ContinuedStay:
          this.createContinuedStay.emit();
          break;
        case UploadDialogAction.Level:
          this.levelAdjustment.emit();
          this.inputFile.nativeElement.click();
          break;
        case UploadDialogAction.WithoutRequest:
          this.inputFile.nativeElement.click();
          break;
        default:
          break;
      }
    });
  }
}
